import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PartyPopper, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { useYouTubePlayer } from "@/hooks/useYouTubePlayer";
import { useVideoJob } from "@/hooks/useVideoJob";
import { ProgressStepper } from "@/components/progress-stepper";
import { TranscriptFallback } from "@/components/transcript-fallback";
import { defaultsForCounts } from "@/lib/url";

export function WatchPage() {
  const { videoId = "" } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefilledUrl = params.get("url") ?? undefined;
  const initialCounts = useMemo(() => defaultsForCounts(params.get("counts")), [params]);

  const { summary, error, retry, refresh } = useVideoJob(videoId, "watch", initialCounts);
  const [ended, setEnded] = useState(false);
  const { containerRef, ready, error: playerError } = useYouTubePlayer(videoId, () =>
    setEnded(true),
  );

  useEffect(() => {
    setEnded(false);
  }, [videoId]);

  const countsQuery = `&counts=${initialCounts[1]},${initialCounts[2]},${initialCounts[3]}`;
  const goToQuiz = () => navigate(`/quiz/${videoId}?url=${encodeURIComponent(prefilledUrl ?? "")}${countsQuery}`);
  const level1Available = summary?.levels.some((l) => l.level === 1 && l.available) ?? false;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Watch the video</h1>
          <p className="text-sm text-muted-foreground">
            Your quiz is being prepared in the background.
          </p>
        </div>
        <Badge variant="outline" className="font-mono text-xs">
          {videoId}
        </Badge>
      </div>

      <div className="overflow-hidden rounded-lg border bg-black shadow-sm">
        <div
          ref={containerRef}
          className="aspect-video w-full"
          role="region"
          aria-label="YouTube video player"
        />
      </div>

      <div className="space-y-3">
        {playerError && <Alert variant="destructive">Player error: {playerError}</Alert>}
        {summary?.errorCode === "TRANSCRIPT_NEEDS_BROWSER" ? (
          <TranscriptFallback videoId={videoId} onRefresh={refresh} />
        ) : summary?.status === "failed" ? (
          <Alert variant="destructive">
            <div className="flex flex-col gap-2">
              <span>{summary.error ?? error ?? "The quiz could not be built."}</span>
              <Button variant="secondary" size="sm" className="w-fit" onClick={retry}>
                <RefreshCw />
                Try again
              </Button>
            </div>
          </Alert>
        ) : summary && summary.status !== "ready" ? (
          <div className="rounded-lg border p-4">
            <ProgressStepper status={summary.status} />
            {level1Available && (
              <p className="mt-3 text-center text-sm text-muted-foreground">
                Level 1 is ready — the harder levels are still being written.
              </p>
            )}
          </div>
        ) : null}
      </div>

      {!ready && !playerError && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Skeleton className="size-4 rounded-full" />
          Loading player…
        </div>
      )}

      <div className="flex flex-col items-center justify-center gap-2 py-4 sm:flex-row">
        {ended && summary?.status === "ready" ? (
          <Button size="lg" className="gap-2" onClick={goToQuiz}>
            <PartyPopper />
            Ready to test your knowledge?
          </Button>
        ) : level1Available ? (
          <Button size="lg" className="gap-2" onClick={goToQuiz}>
            <Sparkles />
            Start Level 1 now
          </Button>
        ) : (
          <Button size="lg" variant="secondary" onClick={goToQuiz}>
            Skip to quiz
          </Button>
        )}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        <Link to="/" className="underline underline-offset-4 hover:text-foreground">
          Start a different video
        </Link>
      </p>
    </div>
  );
}